import { useLocation } from "react-router-dom";
import { Star, LogOut, Volume2, VolumeX, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarGroup, 
  SidebarGroupContent, 
  SidebarGroupLabel, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarTrigger, 
  SidebarFooter, 
  SidebarHeader, 
  SidebarSeparator, 
  useSidebar 
} from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LanguageSelector } from "@/components/LanguageSelector";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useGlobalSound } from "@/hooks/useGlobalSound";
import { useFavoriteMenus } from "@/hooks/useFavoriteMenus";
import { useUnreadSupportCount } from "@/hooks/useUnreadSupportCount";
import { cn } from "@/lib/utils";
import { getAdminMenuGroups } from "@/config/adminRoutes";

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isSoundEnabled, toggleSound } = useGlobalSound();
  const { favoriteMenus, toggleFavoriteMenu, isFavorite } = useFavoriteMenus();
  const { unreadCount: supportUnreadCount } = useUnreadSupportCount();

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(t('navigation.logout') || 'ออกจากระบบสำเร็จ');
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error(t('common.error') || 'เกิดข้อผิดพลาด');
    }
  };

  // Get menu groups from centralized config
  const menuGroups = getAdminMenuGroups();
  
  const isActive = (path: string) => currentPath === path;

  // Get favorite menu items
  const allItems = menuGroups.flatMap(group => group.items);
  const favoriteItems = allItems.filter(item => isFavorite(item.path));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="border-b border-border/50 bg-gradient-to-r from-[hsl(var(--admin-primary))]/5 to-transparent">
        <div className="flex items-center justify-between p-4">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--admin-primary))] to-[hsl(var(--admin-purple))] flex items-center justify-center shadow-lg">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="admin-heading-4">Admin Panel</h2>
                <p className="text-xs text-muted-foreground">ระบบจัดการ</p>
              </div>
            </div>
          )}
          <SidebarTrigger />
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {/* Favorite Menus Section */}
        {favoriteItems.length > 0 && (
          <div>
            <SidebarGroup>
              <SidebarGroupLabel className={collapsed ? "hidden" : "text-xs uppercase text-muted-foreground font-bold tracking-wider px-4 py-2 flex items-center gap-2"}>
                <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                <span>เมนูที่ติดดาว</span>
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {favoriteItems.map(item => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    const MenuButton = (
                      <button 
                        onClick={() => navigate(item.path)} 
                        className={cn(
                          "flex items-center rounded-xl transition-all w-full relative group",
                          collapsed 
                            ? "justify-center px-2 py-3 gap-0"
                            : "gap-3 px-4 py-3 text-left",
                          active 
                            ? "bg-gradient-to-r from-[hsl(var(--admin-primary))]/10 to-transparent text-[hsl(var(--admin-primary))] font-semibold border-l-4 border-[hsl(var(--admin-primary))] shadow-sm" 
                            : "text-foreground/80 hover:bg-[hsl(var(--admin-sidebar-hover))] hover:text-foreground hover:shadow-sm"
                        )}
                      >
                        <div className={cn(
                          "relative flex-shrink-0 rounded-lg flex items-center justify-center transition-all",
                          collapsed ? "w-8 h-8" : "w-10 h-10",
                          active 
                            ? "bg-[hsl(var(--admin-primary))]/10 text-[hsl(var(--admin-primary))]" 
                            : "bg-muted/50 text-muted-foreground group-hover:bg-[hsl(var(--admin-primary))]/5 group-hover:text-[hsl(var(--admin-primary))]"
                        )}>
                          <Icon className="h-5 w-5" />
                          {collapsed && <Star className="h-2.5 w-2.5 fill-yellow-500 text-yellow-500 absolute -top-1 -right-1" />}
                        </div>
                        {!collapsed && (
                          <>
                            <span className="text-sm flex-1 font-medium">{item.label}</span>
                            <button 
                              onClick={e => {
                                e.stopPropagation();
                                toggleFavoriteMenu(item.path);
                              }} 
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Star className="h-4 w-4 fill-yellow-500 text-yellow-500 hover:scale-110 transition-transform" />
                            </button>
                          </>
                        )}
                      </button>
                    );
                    
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild>
                          {collapsed ? (
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                {MenuButton}
                              </TooltipTrigger>
                              <TooltipContent side="right" className="font-normal text-black dark:text-white">
                                {item.label}
                              </TooltipContent>
                            </Tooltip>
                          ) : MenuButton}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <SidebarSeparator className="my-2" />
          </div>
        )}

        {/* Regular Menu Groups */}
        {menuGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <SidebarGroup>
              <SidebarGroupLabel className={collapsed ? "hidden" : "text-xs uppercase text-muted-foreground font-bold tracking-wider px-4 py-2"}>
                {group.title}
              </SidebarGroupLabel>
              
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map(item => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    const MenuButton = (
                      <button 
                        onClick={() => navigate(item.path)} 
                        className={cn(
                          "flex items-center rounded-xl transition-all w-full relative group",
                          collapsed 
                            ? "justify-center px-2 py-3 gap-0"
                            : "gap-3 px-4 py-3 text-left",
                          active 
                            ? "bg-gradient-to-r from-[hsl(var(--admin-primary))]/10 to-transparent text-[hsl(var(--admin-primary))] font-semibold border-l-4 border-[hsl(var(--admin-primary))] shadow-sm" 
                            : "text-foreground/80 hover:bg-[hsl(var(--admin-sidebar-hover))] hover:text-foreground hover:shadow-sm"
                        )}
                      >
                        <div className={cn(
                          "relative flex-shrink-0 rounded-lg flex items-center justify-center transition-all",
                          collapsed ? "w-8 h-8" : "w-10 h-10",
                          active 
                            ? "bg-[hsl(var(--admin-primary))]/10 text-[hsl(var(--admin-primary))]" 
                            : "bg-muted/50 text-muted-foreground group-hover:bg-[hsl(var(--admin-primary))]/5 group-hover:text-[hsl(var(--admin-primary))]"
                        )}>
                          <Icon className="h-5 w-5" />
                          {collapsed && isFavorite(item.path) && (
                            <Star className="h-2.5 w-2.5 fill-yellow-500 text-yellow-500 absolute -top-1 -right-1" />
                          )}
                        </div>
                        {!collapsed && (
                          <>
                            <span className="text-sm flex-1 font-medium">{item.label}</span>
                            {item.path === '/admin/support' && supportUnreadCount > 0 && (
                              <Badge 
                                variant="destructive" 
                                className="min-w-5 h-5 text-xs px-1.5 flex items-center justify-center animate-pulse"
                              >
                                {supportUnreadCount > 99 ? '99+' : supportUnreadCount}
                              </Badge>
                            )}
                            <button 
                              onClick={e => {
                                e.stopPropagation();
                                toggleFavoriteMenu(item.path);
                              }} 
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Star className={cn(
                                "h-4 w-4 transition-all hover:scale-110",
                                isFavorite(item.path) 
                                  ? "fill-yellow-500 text-yellow-500" 
                                  : "text-muted-foreground hover:text-yellow-500"
                              )} />
                            </button>
                          </>
                        )}
                      </button>
                    );
                    
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild>
                          {collapsed ? (
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                {MenuButton}
                              </TooltipTrigger>
                              <TooltipContent side="right" className="font-normal text-black dark:text-white">
                                {item.label}
                              </TooltipContent>
                            </Tooltip>
                          ) : MenuButton}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            {groupIndex < menuGroups.length - 1 && <SidebarSeparator className="my-2" />}
          </div>
        ))}
      </SidebarContent>
      
      <SidebarFooter className="border-t border-border/50">
        <div className={cn("p-4", collapsed ? "flex flex-col items-center gap-2" : "space-y-3")}>
          {/* Sound Toggle */}
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={collapsed ? "icon" : "sm"}
                onClick={toggleSound}
                className={cn(
                  "transition-all",
                  collapsed ? "w-10 h-10" : "w-full justify-start gap-2"
                )}
              >
                {isSoundEnabled ? (
                  <Volume2 className="h-4 w-4 text-green-500" />
                ) : (
                  <VolumeX className="h-4 w-4 text-muted-foreground" />
                )}
                {!collapsed && (
                  <span className="text-sm">
                    {isSoundEnabled ? "เสียงเปิด" : "เสียงปิด"}
                  </span>
                )}
              </Button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right">
                {isSoundEnabled ? "เสียงเปิด" : "เสียงปิด"}
              </TooltipContent>
            )}
          </Tooltip>

          {/* Theme Toggle */}
          {!collapsed && <ThemeToggle />}

          {/* Language Selector */}
          {!collapsed && <LanguageSelector />}

          {/* Logout Button */}
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="destructive"
                size={collapsed ? "icon" : "sm"}
                onClick={handleLogout}
                className={cn(
                  "transition-all",
                  collapsed ? "w-10 h-10" : "w-full justify-start gap-2"
                )}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span className="text-sm">ออกจากระบบ</span>}
              </Button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right">
                ออกจากระบบ
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
